import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type DateRange, type Locale } from "@/shared/utils";
import { usePagination } from "@/shared/hooks";
import { normalizeDateToStartOfDay, normalizeDateToEndOfDay } from "@/shared/utils";
import { MONTHS_RU, MONTHS_KK, MONTHS_EN } from "@/shared/consts";
import { useGetCombinedTimesheetsQuery } from "../api";
import type { CombinedTimesheetListItem, CombinedTimesheetSummary, CombinedStatus } from "../types";
import { useTimesheetsSort, toggleSort, type SortKey, type SortConfig } from "./useTimesheetsSort";

export interface StatusOption {
  label: string;
  value: CombinedStatus | null;
}

export interface TimesheetStats {
  totalEntries: number;
  totalWorkDays: number;
  totalTaxes: string;
  totalEmployerCost: string;
}

export interface UseTimesheetsListPageReturn {
  data: CombinedTimesheetListItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
  search: string;
  setSearch: (value: string) => void;
  dateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  statusFilter: CombinedStatus | null;
  setStatusFilter: (value: CombinedStatus | null) => void;
  statusOptions: StatusOption[];
  activeFilters: boolean;
  yearFilter: number | null;
  setYearFilter: (value: number | null) => void;
  monthFilter: number | null;
  setMonthFilter: (value: number | null) => void;
  locale: Locale;
  filteredTimesheets: CombinedTimesheetListItem[];
  sortConfig: SortConfig<SortKey> | null;
  handleSort: (key: SortKey) => void;
  pagination: ReturnType<typeof usePagination>;
  pageTimesheets: CombinedTimesheetListItem[];
  handleResetFilters: () => void;
  stats: TimesheetStats;
  t: ReturnType<typeof useTranslation>["t"];
}

export function useTimesheetsListPage(): UseTimesheetsListPageReturn {
  const { i18n, t } = useTranslation("TimesheetsPage");
  const locale = i18n.language as Locale;

  const { data: response, isLoading, isError } = useGetCombinedTimesheetsQuery();

  const data = response?.results;

  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<DateRange>({
    start: null,
    end: null,
  });
  const [statusFilter, setStatusFilter] = useState<CombinedStatus | null>(null);
  const [yearFilter, setYearFilter] = useState<number | null>(null);
  const [monthFilter, setMonthFilter] = useState<number | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig<SortKey> | null>(null);

  const statusOptions = useMemo<StatusOption[]>(() => {
    return [
      { label: t("filters.allStatuses"), value: null },
      { label: t("combinedStatus.draft"), value: "draft" },
      { label: t("combinedStatus.timesheet_approved"), value: "timesheet_approved" },
      { label: t("combinedStatus.calculated"), value: "calculated" },
      { label: t("combinedStatus.pending_payment"), value: "pending_payment" },
      { label: t("combinedStatus.paid"), value: "paid" },
    ];
  }, [t]);

  const stats = useMemo<TimesheetStats>(() => {
    if (!response?.summary) {
      return { totalEntries: 0, totalWorkDays: 0, totalTaxes: "0", totalEmployerCost: "0" };
    }
    return {
      totalEntries: response.summary.total_entries_count,
      totalWorkDays: response.summary.total_work_days,
      totalTaxes: response.summary.total_taxes,
      totalEmployerCost: response.summary.total_employer_cost,
    };
  }, [response?.summary]);

  const activeFilters = useMemo(() => {
    return (
      !!search.trim() ||
      !!dateRange.start ||
      !!dateRange.end ||
      !!statusFilter ||
      yearFilter !== null ||
      monthFilter !== null
    );
  }, [search, dateRange, statusFilter, yearFilter, monthFilter]);

  function handleSort(key: SortKey) {
    setSortConfig((prev) => toggleSort(prev, key));
  }

  const filteredTimesheets = useMemo(() => {
    if (!data) return [];

    return data.filter((timesheet) => {
      const trimmedSearch = search.trim();
      if (trimmedSearch) {
        const searchLower = trimmedSearch.toLowerCase();
        const yearStr = String(timesheet.year);
        const monthStr = String(timesheet.month);

        const monthRu = MONTHS_RU[timesheet.month - 1]?.toLowerCase() || "";
        const monthKk = MONTHS_KK[timesheet.month - 1]?.toLowerCase() || "";
        const monthEn = MONTHS_EN[timesheet.month - 1]?.toLowerCase() || "";
        const monthNameRu = timesheet.month_name_ru?.toLowerCase() || "";

        const periodText = `${monthRu} ${yearStr} ${monthKk} ${monthEn} ${monthNameRu} ${monthStr}`.toLowerCase();

        const matchesSearch =
          yearStr.includes(trimmedSearch) ||
          monthRu.includes(searchLower) ||
          monthKk.includes(searchLower) ||
          monthEn.includes(searchLower) ||
          monthNameRu.includes(searchLower) ||
          monthStr.includes(trimmedSearch) ||
          periodText.includes(searchLower);

        if (!matchesSearch) return false;
      }

      if (statusFilter && timesheet.combined_status !== statusFilter) {
        return false;
      }

      if (dateRange.start || dateRange.end) {
        const monthStart = normalizeDateToStartOfDay(new Date(timesheet.year, timesheet.month - 1, 1));
        const monthEnd = normalizeDateToEndOfDay(new Date(timesheet.year, timesheet.month, 0));

        const rangeStart = dateRange.start ? normalizeDateToStartOfDay(dateRange.start) : null;
        const rangeEnd = dateRange.end ? normalizeDateToEndOfDay(dateRange.end) : null;

        const hasIntersection = (!rangeStart || rangeStart <= monthEnd) && (!rangeEnd || rangeEnd >= monthStart);

        if (!hasIntersection) return false;
      }

      if (yearFilter && timesheet.year !== yearFilter) {
        return false;
      }

      if (monthFilter && timesheet.month !== monthFilter) {
        return false;
      }

      return true;
    });
  }, [data, search, statusFilter, dateRange, yearFilter, monthFilter]);

  const sortedTimesheets = useTimesheetsSort(filteredTimesheets, sortConfig);

  const pagination = usePagination(sortedTimesheets.length, 10, [
    search,
    statusFilter,
    dateRange,
    yearFilter,
    monthFilter,
    sortConfig,
  ]);

  const pageTimesheets = sortedTimesheets.slice(pagination.startIndex, pagination.endIndex);

  function handleResetFilters() {
    setSearch("");
    setDateRange({ start: null, end: null });
    setStatusFilter(null);
    setYearFilter(null);
    setMonthFilter(null);
    pagination.setPage(1);
  }

  return {
    data,
    isLoading,
    isError,
    search,
    setSearch,
    dateRange,
    setDateRange,
    statusFilter,
    setStatusFilter,
    statusOptions,
    activeFilters,
    yearFilter,
    setYearFilter,
    monthFilter,
    setMonthFilter,
    locale,
    filteredTimesheets,
    sortConfig,
    handleSort,
    pagination,
    pageTimesheets,
    handleResetFilters,
    stats,
    t,
  };
}
